import React, { useRef, useState } from 'react';
import { ShoppingCart, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import ImageLightbox from './ImageLightbox';

const formatPrice = (n) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const ProductCard = ({ product, onAdd, showCustomDesign = false }) => {
  const inStock = product.stock === 'In stoc' || product.stock === 'La comanda';
  const isCustomOrder = product.stock === 'La comanda';
  const [zoom, setZoom] = useState(false);
  const handleCustomDesign = () => {
    const text = `Bună! Vreau o ofertă pentru un ${product.name}.\n\nAtașez designul/inspirația mea și aș vrea să discutăm dimensiunile și detaliile.`;
    const url = `https://wa.me/40747825465?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="group bg-white border border-gray-100 rounded-md overflow-hidden hover:shadow-xl hover:border-gray-200 transition-all duration-300 flex flex-col">
      <button
        type="button"
        onClick={() => setZoom(true)}
        className="relative aspect-[4/3] overflow-hidden bg-gray-50 cursor-zoom-in block w-full text-left focus:outline-none focus:ring-2 focus:ring-amber-500"
        aria-label="Mărește imaginea"
      >
        {product.discount > 0 && (
          <span className="absolute top-3 left-3 z-10 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-sm shadow">
            - {product.discount}%
          </span>
        )}
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/90 text-gray-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
          <ZoomIn className="w-4 h-4" />
        </span>
      </button>
      <ImageLightbox open={zoom} src={product.image} alt={product.name} images={product.images} onClose={() => setZoom(false)} />
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm text-gray-800 leading-snug line-clamp-3 min-h-[3.6em] mb-2 group-hover:text-amber-700 transition-colors">
          {product.name}
        </h3>
        <div className="text-[11px] text-gray-500 mb-2">Cod: {product.code}</div>
        <div className={`text-xs font-medium mb-2 ${product.stock === 'In stoc' ? 'text-emerald-600' : (isCustomOrder ? 'text-amber-600' : 'text-red-500')}`}>
          {product.stock}
        </div>
        <div className="mt-auto">
          <div className="flex items-baseline gap-2 mb-3">
            {product.oldPrice && (
              <span className="text-xs text-gray-400 line-through">{formatPrice(product.oldPrice)} lei</span>
            )}
            <span className="text-lg font-bold text-gray-900">
              {formatPrice(product.price)} lei
              {product.priceUnit && <span className="text-xs font-normal text-gray-500 ml-1">/ {product.priceUnit}</span>}
            </span>
          </div>
          <button
            onClick={() => onAdd && onAdd(product)}
            disabled={!inStock}
            className="w-full flex items-center justify-center gap-2 bg-[#1a1a1a] hover:bg-amber-500 text-white text-xs font-semibold uppercase tracking-wider py-2.5 rounded-sm transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4" />
            {!inStock ? 'Indisponibil' : (isCustomOrder ? 'Cere oferta' : 'Adauga in cos')}
          </button>
          {showCustomDesign && (
            <button
              type="button"
              onClick={handleCustomDesign}
              className="mt-2 w-full flex items-center justify-center gap-2 bg-white border-2 border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white text-xs font-semibold uppercase tracking-wider py-2.5 rounded-sm transition-colors"
              data-testid={`custom-design-${product.id}`}
            >
              Trimite designul tău
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductCarousel = ({ products, onAdd, showCustomDesign = false }) => {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (!ref.current) return;
    const w = ref.current.clientWidth;
    ref.current.scrollBy({ left: dir * w * 0.8, behavior: 'smooth' });
  };
  return (
    <div className="relative">
      <button
        onClick={() => scroll(-1)}
        className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-md hover:bg-amber-500 hover:text-white hover:border-amber-500 items-center justify-center transition-colors"
        aria-label="Scroll left"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <div
        ref={ref}
        className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2"
        style={{ scrollbarWidth: 'none' }}
      >
        {products.map((p) => (
          <div key={p.id} className="flex-shrink-0 w-[260px] md:w-[280px] snap-start">
            <ProductCard product={p} onAdd={onAdd} showCustomDesign={showCustomDesign} />
          </div>
        ))}
      </div>
      <button
        onClick={() => scroll(1)}
        className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-md hover:bg-amber-500 hover:text-white hover:border-amber-500 items-center justify-center transition-colors"
        aria-label="Scroll right"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export default ProductCarousel;
